/**
 * Яндекс.Метрика: инициализация счётчика и отправка целей.
 *
 * Счётчик тот же, что стоит на текущем about.ris.promo, — сайт переезжает
 * на тот же домен, и статистика не разрывается. Переопределяется переменной
 * VITE_YM_COUNTER_ID на случай отдельного счётчика под редизайн.
 *
 * Все вызовы безопасны: если скрипт не загрузился (блокировщик, офлайн),
 * цели просто уходят в очередь и никуда не отправляются — падать нечему.
 */
const COUNTER_ID = Number(import.meta.env.VITE_YM_COUNTER_ID ?? 103353448)

type YmFunction = ((...args: unknown[]) => void) & { a?: unknown[][]; l?: number }

declare global {
  interface Window {
    ym?: YmFunction
  }
}

/** Названия целей держим в одном месте: в интерфейсе Метрики они заводятся руками,
 *  и разъезд «в коде одно, в кабинете другое» ловится только глазами. */
export const GOALS = {
  /** Клик по любой кнопке регистрации. Параметр place — какая именно. */
  signupClick: 'signup_click',
  /** Открыли окно «Напомнить позже». */
  reminderOpen: 'reminder_open',
  /** Оставили почту, заявка ушла. */
  reminderSent: 'reminder_sent',
  /** Заявка не ушла — видно, что форма ломается, а не что она никому не нужна. */
  reminderError: 'reminder_error',
  /** Переход на витрину ris.promo. */
  showcaseClick: 'showcase_click',
  /** Открыли документ из футера. Параметр doc — какой. */
  docOpen: 'doc_open',
  /** Клик по пункту меню в шапке. Параметр item — какой. */
  navClick: 'nav_click',
  /** Секция впервые попала в экран. Параметр section — какая. */
  sectionView: 'section_view',
} as const

export type GoalName = (typeof GOALS)[keyof typeof GOALS]

/** Вставляет тег Метрики. Вызывать один раз при старте приложения. */
export function initMetrika() {
  if (typeof window === 'undefined' || window.ym) return

  const ym: YmFunction = (...args: unknown[]) => {
    ;(ym.a = ym.a || []).push(args)
  }
  ym.l = Date.now()
  window.ym = ym

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://mc.yandex.ru/metrika/tag.js'
  document.head.appendChild(script)

  window.ym(COUNTER_ID, 'init', {
    // clickmap и webvisor — то, ради чего это всё: видно, куда жмут и как
    // скроллят. trackLinks считает уходы на lk.ris.promo и ris.promo.
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true,
    trackHash: true,
  })
}

/** Отправляет цель. params попадают в отчёт «Параметры визитов». */
export function reachGoal(goal: GoalName, params?: Record<string, string>) {
  window.ym?.(COUNTER_ID, 'reachGoal', goal, params)
}
